import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import QRCode from 'qrcode';
import { FiArrowLeft, FiDownload, FiShare2, FiCopy, FiDollarSign, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/helpers';

const PersonalQrScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(true);
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, [user, userType]);

  const loadProfile = async () => {
    if (!user) return;
    setLoading(true);
    try {
      let profileData = null;
      if (userType === 'provider') {
        profileData = await supabaseService.getProviderById(user.id);
      } else {
        profileData = await supabaseService.getClientById(user.id);
      }
      setProfile(profileData);
      if (profileData?.walletCode) {
        generateQrCode(profileData.walletCode, profileData);
      } else {
        toast.error('لا يوجد رمز محفظة لهذا الحساب');
        setGenerating(false);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('فشل تحميل معلومات الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  const generateQrCode = async (walletCode, profileData) => {
    setGenerating(true);
    try {
      const qrData = `khidma_wallet:${walletCode}`;
      const url = await QRCode.toDataURL(qrData, {
        width: 400,
        margin: 2,
        color: {
          dark: '#1e293b',
          light: '#ffffff',
        },
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Failed to generate QR Code:', err);
      toast.error('فشل توليد رمز QR');
    } finally {
      setGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.download = `wallet_qr_${profile?.walletCode || 'my'}.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('تم تحميل رمز QR بنجاح');
  };

  const shareQRCode = async () => {
    if (!qrCodeUrl) return;
    if (navigator.share) {
      try {
        const response = await fetch(qrCodeUrl);
        const blob = await response.blob();
        const file = new File([blob], `wallet_qr_${profile?.walletCode || 'my'}.png`, { type: 'image/png' });
        await navigator.share({
          files: [file],
          title: 'رمز محفظتي في خدمة',
          text: `هذا هو رمز محفظتي في تطبيق خدمة. أرسل لي المال بمسح الرمز!`,
        });
        toast.success('تمت المشاركة بنجاح');
      } catch (err) {
        console.error('Error sharing QR Code:', err);
      }
    } else {
      downloadQRCode();
    }
  };

  const copyWalletCode = () => {
    if (profile?.walletCode) {
      navigator.clipboard.writeText(profile.walletCode).then(() => {
        toast.success('تم نسخ رمز المحفظة');
      }).catch(() => {
        toast.error('فشل نسخ الرمز');
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition active:scale-95"
        >
          <FiArrowLeft className="text-white w-5 h-5" />
        </button>
        <h1 className="text-white font-bold text-lg">رمز المحفظة الشخصي</h1>
        <div className="w-9" /> {/* spacer */}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
        <div className="w-full max-w-sm">
          {/* User Info Card */}
          {profile && (
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <img
                  src={profile.avatarUrl || 'https://via.placeholder.com/150'}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full border-4 border-white/30 shadow-lg mx-auto mb-3 object-cover"
                />
              </div>
              <h2 className="text-white text-xl font-bold">{profile.fullName || 'مستخدم'}</h2>
              <p className="text-gray-400 text-sm mt-1">
                {userType === 'provider' ? 'مزود خدمة' : 'زبون'}
              </p>
            </div>
          )}

          {/* QR Code Card */}
          <div className="bg-white rounded-3xl p-6 shadow-2xl text-center">
            <div className="mb-3">
              <span className="inline-block bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full">
                رمز التحويل الشخصي
              </span>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-4 inline-block">
              {generating ? (
                <div className="w-56 h-56 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR Code" className="w-56 h-56 mx-auto" />
              ) : (
                <div className="w-56 h-56 flex items-center justify-center text-gray-400">
                  لا يوجد رمز
                </div>
              )}
            </div>

            {/* Wallet Code Display */}
            <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center justify-between gap-2">
              <span
                className="text-gray-700 font-mono text-sm font-bold truncate"
                dir="ltr"
              >
                {profile?.walletCode || '------'}
              </span>
              <button
                onClick={copyWalletCode}
                className="flex-shrink-0 p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition active:scale-95"
                title="نسخ رمز المحفظة"
              >
                <FiCopy className="w-4 h-4" />
              </button>
            </div>

            {/* Wallet Balance */}
            <div className="bg-green-50 rounded-xl p-4 mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-800">
                <FiDollarSign className="w-5 h-5 text-green-600" />
                <span className="text-sm font-bold">رصيد المحفظة</span>
              </div>
              <span className="text-lg font-extrabold text-green-700">
                {formatCurrency(profile?.wallet || 0)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadQRCode}
                disabled={!qrCodeUrl}
                className="flex items-center justify-center gap-2 p-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition active:scale-95 disabled:opacity-50"
              >
                <FiDownload className="w-4 h-4" />
                تحميل
              </button>
              <button
                onClick={shareQRCode}
                disabled={!qrCodeUrl}
                className="flex items-center justify-center gap-2 p-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition active:scale-95 disabled:opacity-50"
              >
                <FiShare2 className="w-4 h-4" />
                مشاركة
              </button>
            </div>
          </div>

          {/* Info Text */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-xs leading-relaxed">
              شارك هذا الرمز مع الآخرين ليتمكنوا من تحويل الأموال إلى محفظتك مباشرة.
              عند مسح الرمز، سيتم فتح نافذة إدخال المبلغ المراد تحويله.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalQrScreen;